import React, { useEffect, useState } from 'react';
import Layout from '../components/layout/AppLayout';
import Header from '../../shared/components/layout/Header';
import FormInput from '../../shared/components/ui/FormInput';
import {
  getAllSalesArsByProjectId,
  getAllActiveSalesUsersByProjectId,
  getLeadsByStatus,
  addTargets,
  getAllSalesTargetsByProjectId,
  updateTargets,
  addIncentives,
  getIncentives,
  updateIncentives
} from '../services/api/implementation/impl';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useSidebar } from '../../shared/contexts/SidebarContext';
import ArrowIcon from '../../shared/assets/svg/arrow-large-right.svg';
import Button from '../../shared/components/ui/Button';
import Pencil from '../../shared/assets/svg/pencil.svg';
import NoTaskIcon from '../../shared/assets/svg/NoTasks.svg';
import Modal from '../../shared/components/ui/Modal';
import toast from 'react-hot-toast';
import Cookies from 'js-cookie';
import mixpanel from 'mixpanel-browser';
import { useProjectsList } from '../../../contexts/ProjectsContext';
import AiSectionBanner from '../components/ai/AiSectionBanner';

const TargetsAndIncentives = ({ embedded = false }) => {
  const userId = Cookies.get('userId');
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const { collapsed } = useSidebar();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAddTargetModal, setShowAddTargetModal] = useState(false);
  const [showAddIncentivesModal, setShowAddIncentivesModal] = useState(false);
  const [users, setUsers] = useState([]);
  const [formData, setFormData] = useState({});
  const [month, setMonth] = useState('');
  const [newLeadWeightage, setNewLeadWeightage] = useState('');
  const [stages, setStages] = useState([{ name: '', percentage: '' }]);
  const [arWeightage, setArWeightage] = useState('');
  const [arType, setArType] = useState('Variable');
  const [minCap, setMinCap] = useState('');
  const [maxCap, setMaxCap] = useState('');
  const [modalType, setModalType] = useState('');
  const [arMappingId, setArMappingId] = useState(null);
  const [overallIncentiveId, setOverallIncentiveId] = useState(null);

  React
    // Track page view
    .useEffect(() => {
      mixpanel.track('Targets And Incentives Viewed', {
        user_id: userId,
        timestamp: new Date().toISOString()
      });
    }, [userId]);

  const {
    register,
    formState: { errors },
    setValue,
    watch
  } = useForm({
    defaultValues: {
      project: projects?.[0]?.id || ''
    }
  });

  const projectID = watch('project');
  React.useEffect(() => {
    if (projects) {
      setValue('project', projects?.[0]?.id);
    }
  }, [projects, setValue]);
  useEffect(() => {
    const today = new Date();
    // const currentMonth = new Date().getMonth() + 1; // getMonth() is 0-based
    const initialMonth = today.getMonth() + 1;
    setMonth(initialMonth); // could also use month name if needed
  }, []);

  const {
    data: allSales,
    refetch: refetchSales,
    isFetching
  } = useQuery({
    queryKey: ['sales', projectID],
    queryFn: () => getAllActiveSalesUsersByProjectId(projectID, 1, 1000),
    enabled: !!projectID
  });

  const { data: allARs, refetch: refetchArs } = useQuery({
    queryKey: ['Ars', projectID, month],
    queryFn: () => getAllSalesArsByProjectId(projectID, month),
    enabled: !!projectID && !!month
  });

  const { data: incentives, refetch: refetchIncentives } = useQuery({
    queryKey: ['incentives', projectID],
    queryFn: () => getIncentives(projectID),
    enabled: !!projectID
  });

  const { data: allTargets, refetch: refetTargets } = useQuery({
    queryKey: ['allTargets', projectID, month],
    queryFn: () => {
      if (!projectID || !month) return Promise.resolve(null); // Handle undefined cases
      return getAllSalesTargetsByProjectId(projectID, month);
    },
    enabled: !!projectID && !!month
  });
  // Mock API call - replace with real fetch
  useEffect(() => {
    const fetchUsers = async () => {
      const userID = Number(Cookies.get('userId'));

      // Filter out the current user instead of sorting to top
      const data = allSales?.data
        ?.filter((item) => item.id !== userID) // Remove the current user
        ?.map((item) => ({ id: item.id, name: item.name }));

      setUsers(data);

      // Initialize form data without the current user
      const initialData = {};
      data?.forEach((user) => {
        const matchedAR = allARs?.data?.find((ar) => ar.id === Number(user.id));
        const arValue = matchedAR ? matchedAR.ar : 0;

        initialData[user.id] = {
          target_amount: '',
          ar_target: arValue.toString(),
          incentive: ''
        };
      });

      setFormData(initialData);
    };

    fetchUsers();
  }, [allSales, allARs]);

  const handleChange = (id, field, value) => {
    // Allow toggling of boolean field 'selected' without validation
    if (field === 'selected') {
      setFormData((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          [field]: !prev[id][field]
        }
      }));
      return;
    }

    // Prevent non-numeric input
    if (!/^\d*$/.test(value)) {
      return; // Ignore the update if value is not numeric
    }

    setFormData((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };
  const handleIncentiveChange = (id, field, value) => {
    // Allow toggling of boolean field 'selected' without validation
    if (field === 'selected') {
      setFormData((prev) => ({
        ...prev,
        [id]: {
          ...prev[id],
          [field]: !prev[id][field]
        }
      }));
      return;
    }

    // Prevent non-numeric input
    if (!/^\d*\.?\d*$/.test(value)) {
      return; // Ignore the update if value is not numeric or decimal
    }

    setFormData((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value
      }
    }));
  };

  const handleSave = () => {
    const incompleteUsers = [];

    const payload = Object.entries(formData).map(([userId, data]) => {
      const { target_amount, incentive, ar_target, target_id } = data;

      // Remove "₹" prefix if present and convert to number
      const cleanedArTarget =
        typeof ar_target === 'string'
          ? Number(ar_target.replace(/[^0-9.-]+/g, ''))
          : Number(ar_target);

      // Validation: check for empty target_amount or incentive
      if (!target_amount || !incentive) {
        incompleteUsers.push(userId);
      }

      const year = new Date().getFullYear();
      const monthPadded = String(month).padStart(2, '0');
      const monthDateIso = `${year}-${monthPadded}-01T00:00:00.000Z`;

      const payloadItem = {
        sales_user_id: Number(userId),
        target_amount: Number(target_amount),
        incentive: Number(incentive),
        ar_target: cleanedArTarget,
        project_id: Number(projectID),
        month_date: monthDateIso
      };

      // Only include target_id if it exists (for edit operations)
      if (target_id) {
        payloadItem.id = target_id;
      }

      return payloadItem;
    });

    if (incompleteUsers.length > 0) {
      toast.error('Please fill in both target and incentive for all users before saving.');
      return;
    }

    console.log('Payload to be sent:', payload);
    if (modalType === 'Add') {
      handleAddTarget(payload);
    }
    if (modalType === 'Edit') {
      handleUpdateTarget(payload);
    }
  };

  const { mutate: handleUpdateTarget } = useMutation({
    mutationFn: (payload) => updateTargets(payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Targets Updated Successfully');
      setModalType('');
      setShowAddTargetModal(false);
      await refetTargets();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error updating target', err);
      const errorMessage = err.response?.data?.message || err?.message || 'Error updating target';
      toast.error(errorMessage);
    }
  });

  const { mutate: handleAddTarget } = useMutation({
    mutationFn: (payload) => addTargets(payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Targets Added Successfully');
      setShowAddTargetModal(false);
      setModalType('');
      await refetTargets();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error adding target', err);
      const errorMessage = err.response?.data?.message || err?.message || 'Error adding target';
      toast.error(errorMessage);
    }
  });

  const handleStageChange = (index, key, value) => {
    const updatedStages = [...stages];
    updatedStages[index][key] = value;
    setStages(updatedStages);
  };

  const handleAddStage = () => {
    setStages([...stages, { name: '', percentage: '' }]);
  };

  const handleRemoveStage = (index) => {
    setStages(stages.filter((_, i) => i !== index));
  };
  const validateForm = () => {
    // Validate numeric fields (weightage percentages)
    const isNumeric = (value) => /^\d*\.?\d+$/.test(value);

    // 1. Validate New Bookings section
    if (!newLeadWeightage || !isNumeric(newLeadWeightage)) {
      toast.error('Please enter a valid weightage percentage for New Bookings');
      return false;
    }

    if (parseFloat(newLeadWeightage) + parseFloat(arWeightage) !== 100) {
      toast.error('Sum of New Booking Weightage and AR Weightage must be exactly 100');
      return false;
    }
    const totalStagePercentage = stages.reduce(
      (sum, stage) => sum + (parseFloat(stage.percentage) || 0),
      0
    );
    if (totalStagePercentage !== 100) {
      toast.error('Sum of all stage percentages must be exactly 100');
      return false;
    }

    // Validate stages
    for (const [index, stage] of stages.entries()) {
      if ((stage.name && !stage.percentage) || (!stage.name && stage.percentage)) {
        toast.error(`Please complete both name and percentage for stage ${index + 1}`);
        return false;
      }

      if (stage.percentage && !isNumeric(stage.percentage)) {
        toast.error(`Please enter a valid percentage for stage ${index + 1}`);
        return false;
      }
    }

    // 2. Validate AR Incentive section
    if (!arWeightage || !isNumeric(arWeightage)) {
      toast.error('Please enter a valid weightage percentage for AR Incentive');
      return false;
    }

    if (arType === 'Variable') {
      if (!minCap || !isNumeric(minCap)) {
        toast.error('Please enter a valid minimum amount');
        return false;
      }
      if (!maxCap || !isNumeric(maxCap)) {
        toast.error('Please enter a valid maximum amount');
        return false;
      }
      if (parseFloat(minCap) >= parseFloat(maxCap)) {
        toast.error('Maximum amount must be greater than minimum amount');
        return false;
      }
      if (parseFloat(minCap) >= 100) {
        toast.error('Minimum Cap should be less than 100');
        return false;
      }
      if (parseFloat(maxCap) <= 100) {
        toast.error('Maximum Cap should be more than 100');
        return false;
      }
    } else {
      // Reset caps if Fixed type is selected
      setMinCap('');
      setMaxCap('');
    }

    return true;
  };

  const handleSaveIncentivesDetails = () => {
    if (!validateForm()) return;

    const payload = {
      project_id: Number(projectID),
      bookingwaightage: parseFloat(newLeadWeightage) || 0,
      arwaightage: parseFloat(arWeightage) || 0,

      // Pass AR incentive with id if editing
      arincentive: {
        id: modalType === 'Edit' ? arMappingId : undefined, // <- include only in edit
        type: arType || null,
        minimum_amount: arType === 'Variable' ? parseFloat(minCap) || 0 : 0,
        maximum_amount: arType === 'Variable' ? parseFloat(maxCap) || 0 : 0
      },

      // Booking incentive stages with IDs
      newbookingincentive: stages.map((stage, index) => ({
        id: modalType === 'Edit' ? stage.id : undefined, // <- include only in edit
        name: stage.name,
        percentage: parseFloat(stage.percentage) || 0,
        srNo: index + 1
      })),

      // Include overall incentive ID if available (optional)
      id: modalType === 'Edit' ? overallIncentiveId : undefined
    };

    console.log('Payload:', payload);

    if (modalType === 'Edit') {
      handleUpdateIncentives(payload);
    } else {
      handleIncentives(payload);
    }
  };

  const { mutate: handleUpdateIncentives } = useMutation({
    mutationFn: (payload) => updateIncentives(incentives?.data?.[0]?.id, payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Incentive Updated Successfully');
      setShowAddIncentivesModal(false);
      setNewLeadWeightage('');
      setStages([{ name: '', percentage: '' }]);
      setArWeightage('');
      setArType('Variable');
      setMinCap('');
      setMaxCap('');
      setModalType('');
      await refetchIncentives();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error updating incentive', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error updating incentive');
    }
  });

  const { mutate: handleIncentives } = useMutation({
    mutationFn: (payload) => addIncentives(payload),
    onMutate: () => {
      setIsSubmitting(true); // Disable the button when mutation starts
    },
    onSuccess: async () => {
      setIsSubmitting(false);
      toast.success('Incentive Added Successfully');
      setShowAddIncentivesModal(false);
      setNewLeadWeightage('');
      setStages([{ name: '', percentage: '' }]);
      setArWeightage('');
      setArType('Variable');
      setMinCap('');
      setMaxCap('');
      setModalType('');
      await refetchIncentives();
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error adding incentive', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error adding incentive');
    }
  });

  const handleEditIncentives = (incentiveData) => {
    setModalType('Edit');
    setShowAddIncentivesModal(true);

    setNewLeadWeightage(String(incentiveData?.bookingwaightage));
    setArWeightage(String(incentiveData?.arwaightage));
    setArType(incentiveData?.arMappings?.[0]?.type || 'Variable');

    if (incentiveData?.arMappings?.[0]?.type === 'Variable') {
      setMinCap(String(incentiveData?.arMappings?.[0]?.minimum_amount || ''));
      setMaxCap(String(incentiveData?.arMappings?.[0]?.maximum_amount || ''));
    } else {
      setMinCap('');
      setMaxCap('');
    }

    // Include booking and ar mapping IDs
    setArMappingId(incentiveData?.arMappings?.[0]?.id); // <-- Add this to state if not already done

    setStages(
      (incentiveData?.booking || []).map((stage) => ({
        id: stage.id, // preserve stage id
        name: stage.name,
        percentage: String(stage.percentage)
      }))
    );

    setOverallIncentiveId(incentiveData?.id); // <-- Optional: if there's an overall ID
  };

  const handleNumericInput = (value) => {
    return value.replace(/[^\d.]/g, '');
  };

  const handleMonthChange = (value) => {
    // value is 1–12 as passed from the dropdown
    setMonth(value);
  };

  const handleEditTargets = () => {
    const userID = Number(Cookies.get('userId'));

    const data = allSales?.data
      ?.map((item) => ({ id: item.id, name: item.name }))
      ?.sort((a, b) => {
        if (a.id === userID) return -1;
        if (b.id === userID) return 1;
        return 0;
      });

    setUsers(data);

    // Initialize form data with matched AR and target ID
    const initialData = {};
    data?.forEach((user) => {
      const matchedAR = allARs?.data?.find((ar) => ar.id === Number(user.id));
      const matchedUser = allTargets?.data?.find((u) => u.sales_user_id === Number(user.id));
      const arValue = matchedAR ? matchedAR.ar : 0;

      initialData[user.id] = {
        target_amount: matchedUser?.target_amount,
        ar_target: matchedUser?.ar_target,
        incentive: matchedUser?.incentive,
        target_id: matchedUser?.id // Store the target ID
      };
    });

    setFormData(initialData);
    setModalType('Edit');
    setShowAddTargetModal(true);
  };
  const modals = (
    <>
      {showAddTargetModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={70}
          name={modalType === 'Add' ? 'Set Targets' : 'Edit Targets'}
          onClose={() => setShowAddTargetModal(false)}>
          <div className="p-6">
            {/* Dropdowns */}
            <div className="gap-4 mb-6 flex flex-row justify-start items-center">
              <input
                type="text"
                className="border rounded px-4 py-2 w-[30%] bg-gray-100 cursor-not-allowed"
                value={
                  month
                    ? new Date(new Date().getFullYear(), Number(month) - 1).toLocaleString(
                        'default',
                        {
                          month: 'long'
                        }
                      )
                    : ''
                }
                disabled
              />

              <input
                type="text"
                className="border rounded px-4 py-2 w-[30%] bg-gray-100 cursor-not-allowed"
                value={new Date().getFullYear()}
                disabled
              />
            </div>

            {/* Table */}
            <div className="overflow-x-auto border border-gray-300 rounded-md">
              <table className="min-w-full table-auto text-sm">
                <thead className="bg-gray-100 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left">Name</th>
                    <th className="px-4 py-3 text-left">New Booking Target</th>
                    <th className="px-4 py-3 text-left">AR Target</th>
                    <th className="px-4 py-3 text-left">Incentive(In %)</th>
                  </tr>
                </thead>
                <tbody>
                  {users?.map((user) => (
                    <tr key={user.id} className="border-b">
                      <td className="px-4 py-3">{user.name}</td>
                      <td className="px-4 py-3">
                        <input
                          type="text"
                          className="border px-3 py-1 w-full rounded"
                          placeholder="Enter Target"
                          value={formData[user.id]?.target_amount || ''}
                          onChange={(e) => handleChange(user.id, 'target_amount', e.target.value)}
                        />
                      </td>
                      <td className="px-4 py-3">
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            className="border px-3 py-1 w-full rounded outline-none"
                            value={formData[user.id]?.ar_target || ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^\d.]/g, '');
                              setFormData((prev) => ({
                                ...prev,
                                [user.id]: {
                                  ...prev[user.id],
                                  ar_target: value
                                }
                              }));
                            }}
                          />
                        </td>
                      </td>
                      <td className="px-4 py-3">
                        <div className="relative w-full">
                          <input
                            type="text"
                            className="border px-3 py-1 w-full rounded pr-8" // Add right padding for the % symbol
                            placeholder="In Percentage"
                            value={formData[user.id]?.incentive || ''}
                            onChange={(e) =>
                              handleIncentiveChange(user.id, 'incentive', e.target.value)
                            }
                            maxLength={5}
                          />
                          <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500">
                            %
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Footer Buttons */}
            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button
                type="button"
                onClick={() => setShowAddTargetModal(false)}
                displayType="secondary">
                Cancel
              </Button>
              <Button type="submit" onClick={handleSave}>
                {modalType === 'Add' ? 'Save' : 'Update'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {showAddIncentivesModal && (
        <Modal
          isDirty={true}
          confirmOnClose={true}
          width={60}
          name={
            modalType === 'Add'
              ? 'Add Incentive Details'
              : modalType && modalType === 'Edit' && 'Update Incentive Details'
          }
          onClose={() => {
            setShowAddIncentivesModal(false);
            setModalType('');
            setNewLeadWeightage('');
            setStages([{ name: '', percentage: '' }]);
            setArWeightage('');
            setArType('Variable');
            setMinCap('');
            setMaxCap('');
          }}>
          <div className="p-6">
            <div className="grid grid-cols-[1fr_auto_1fr] gap-x-4 items-stretch">
              {/* New Lead Incentive Details */}
              <div>
                <h2 className="font-semibold text-lg mb-4">New Bookings Incentive Details</h2>
                <div className="flex flex-col gap-2 text-gray-500">
                  <label htmlFor="weightage">Weightage Percentage</label>
                  <div className="relative w-full">
                    <input
                      type="text"
                      className="border w-full px-3 py-2 rounded mb-4"
                      placeholder="Enter Weightage Percentage"
                      value={newLeadWeightage}
                      onChange={(e) => {
                        setNewLeadWeightage(handleNumericInput(e.target.value));
                        setArWeightage(100 - handleNumericInput(e.target.value));
                      }}
                      maxLength={2}
                    />
                    <span className="absolute right-2 top-1/3 transform -translate-y-1/2 text-gray-500">
                      %
                    </span>
                  </div>
                </div>

                <div className="border rounded">
                  <div className="flex font-semibold bg-gray-100 px-3 py-2 border-b">
                    <div className="w-1/2 ">Name</div>
                    <div className="w-1/3">Percentage</div>
                    <div className="w-1/6 text-right">Actions</div>
                  </div>

                  {stages?.map((stage, index) => (
                    <div key={index} className="flex items-center px-3 py-2 border-b">
                      <input
                        type="text"
                        className="w-1/2 border px-2 py-1 rounded mr-2"
                        placeholder="Incentive Stage Name"
                        value={stage.name}
                        onChange={(e) => handleStageChange(index, 'name', e.target.value)}
                      />
                      <div className="relative w-1/3 mr-4">
                        <input
                          type="text"
                          className=" w-full border px-2 py-1 rounded "
                          placeholder="Percentage"
                          value={stage.percentage}
                          onChange={(e) =>
                            handleStageChange(
                              index,
                              'percentage',
                              handleNumericInput(e.target.value)
                            )
                          }
                          maxLength={3}
                        />
                        <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500">
                          %
                        </span>
                      </div>

                      <button
                        onClick={() => handleRemoveStage(index)}
                        className="text-red-500 text-lg font-bold">
                        ×
                      </button>
                    </div>
                  ))}

                  <div className="p-3">
                    <button className="text-sm text-blue-600" onClick={handleAddStage}>
                      + Add New Stage Name
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex justify-center items-stretch">
                <div className="border-l border-gray-300 h-full"></div>
              </div>

              {/* AR Incentive Details */}
              <div>
                <h2 className="font-semibold text-lg mb-4">AR Incentive Details</h2>
                <div className="flex gap-2  mb-4 justify-between items-end">
                  <div className="flex flex-col gap-2 text-gray-500">
                    <label htmlFor="weightage">Weightage Percentage</label>

                    <div className="relative w-[120%]">
                      <input
                        type="text"
                        className="border w-full px-3 py-2 rounded"
                        placeholder="Enter Weightage Percentage"
                        value={arWeightage}
                        onChange={(e) => {
                          setArWeightage(handleNumericInput(e.target.value));
                          setNewLeadWeightage(100 - handleNumericInput(e.target.value));
                        }}
                        maxLength={2}
                      />
                      <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500">
                        %
                      </span>
                    </div>
                  </div>

                  <select
                    className="border px-3 py-2 rounded w-[35%] h-[2.6rem]"
                    value={arType}
                    onChange={(e) => setArType(e.target.value)}>
                    <option value="Variable">Variable</option>
                    <option value="Fixed">Fixed</option>
                  </select>
                </div>

                {arType === 'Variable' && (
                  <div className="border rounded">
                    <div className="flex font-semibold bg-gray-100 px-3 py-2 border-b">
                      <div className="w-1/2">Min Cap / Max Cap</div>
                      <div className="w-1/2">Percentage</div>
                    </div>
                    <div className="flex px-3 py-2 border-b">
                      <div className="w-1/2 py-1">Minimum Cap</div>

                      <div className="relative w-1/2">
                        <input
                          type="text"
                          className=" w-full border pl-2 py-1 rounded"
                          placeholder="In Percentage"
                          value={minCap}
                          onChange={(e) => {
                            setMinCap(handleNumericInput(e.target.value));
                          }}
                        />
                        <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500">
                          %
                        </span>
                      </div>
                    </div>
                    <div className="flex px-3 py-2">
                      <div className="w-1/2 py-1">Maximum Cap</div>
                      <div className="relative w-1/2">
                        <input
                          type="text"
                          className="w-full border pl-2 py-1 rounded"
                          placeholder="Enter Amount"
                          value={maxCap}
                          onChange={(e) => setMaxCap(handleNumericInput(e.target.value))}
                        />
                        <span className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500">
                          %
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="w-full flex justify-end items-center space-x-4 mt-8">
              <Button
                type="button"
                onClick={() => {
                  setShowAddIncentivesModal(false);
                  setNewLeadWeightage('');
                  setStages([{ name: '', percentage: '' }]);
                  setArWeightage('');
                  setArType('Variable');
                  setMinCap('');
                  setMaxCap('');
                  setModalType('');
                }}
                displayType="secondary">
                Cancel
              </Button>
              <Button type="submit" onClick={handleSaveIncentivesDetails}>
                Save
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );

  const mainBody = (
    <>
      {/* Header + filters */}
      <div
        className={
          embedded
            ? `fixed top-16 z-[9] border-b border-gray-200 transition-all duration-300 bg-white ${
                collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
              }`
            : `fixed top-0 z-10 transition-all duration-300 bg-white ${
                collapsed ? 'left-[76px] w-[calc(100%-76px)]' : 'left-64 w-[calc(100%-16rem)]'
              }`
        }>
        {!embedded && <Header data={{ name: `Targets & Incentives` }} />}
        <div
          className={
            embedded
              ? 'flex justify-between items-end gap-2 bg-white px-6 py-3 pb-4'
              : `flex justify-between items-end gap-2 fixed bg-white border px-6 py-3 pb-6 mt-[-0.60rem] transition-all duration-300 z-30 ${
                  collapsed ? 'left-[76px] right-0' : 'left-64 right-0'
                }`
          }>
          <div className="flex gap-3 w-[50%] justify-start items-center">
            <FormInput
              width={50}
              label={''}
              register={register}
              register_key={'project'}
              isRequired={false}
              type={'dropdown'}
              options={projectsForDropdown}
              validation={{ required: 'Project is required' }}
              errors={errors}
            />
            <FormInput
              width={26}
              register={register}
              register_key={'month'}
              label={''}
              type={'date-month'}
              isRequired={false}
              onType={(mo) => {
                console.log('Selected month:', mo); // { id: 6, name: 'June' }
                handleMonthChange(mo.id);
              }}
              valuePre={month}
            />
          </div>
          {allTargets?.data && allTargets?.data?.length > 0 && (
            <Button onClick={() => handleEditTargets()}>Edit Target</Button>
          )}
        </div>
      </div>

      <div className={`transition-all duration-300 ease-in-out ${collapsed ? 'ml-[1vw]' : ''}`}>
        <div
          className={`${
            embedded ? 'pt-32' : 'pt-[10rem]'
          } flex flex-col items-start gap-4 pl-5 pr-4 justify-start pb-5`}>
          <div className="w-full max-w-4xl">
            <AiSectionBanner projectId={projectID} vertical="sales" surface="targets" />
          </div>
          <div className="flex w-full items-start gap-7 justify-start">
          {/* Left Scrollable Cards */}
          <div
            className={`flex flex-col  gap-7 transition-all duration-300 ease-in-out ${
              collapsed ? 'w-[65%]' : 'w-[60%]'
            }`}>
            {allTargets?.data && allTargets?.data?.length > 0 ? (
              allTargets?.data?.map((user, idx) => (
                <div key={idx} className="bg-white rounded-lg shadow-sm p-6 w-[100%]">
                  <h2 className="font-semibold text-lg mb-4">{user.user?.name}</h2>
                  <div className="grid grid-cols-2 gap-10 text-sm text-gray-700 pr-3 pl-3">
                    <div className="border-b-2 border-gray-200">
                      <p className="text-gray-400">New Booking Target</p>
                      <p className="font-medium">{user.target_amount}</p>
                    </div>
                    <div className="border-b-2 border-gray-200">
                      <p className="text-gray-400">AR Target</p>
                      <p className="font-medium">{user.ar_target}</p>
                    </div>
                    <div className="flex justify-center items-center mt-6 gap-10">
                      <p className="text-gray-600 font-medium">Incentive</p>

                      <img src={ArrowIcon} alt="arrow" className="w-5 h-5" />
                      <span className="text-lg font-bold">{user.incentive}%</span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="w-full h-[75vh] bg-[#FBFAFB] rounded-lg flex flex-col justify-center items-center">
                <img className="h-[15rem] w-[15rem]" src={NoTaskIcon} alt="" />
                <p className="text-gray-500">You do not have any targets yet.</p>
                <button
                  onClick={() => {
                    setShowAddTargetModal(true);
                    setModalType('Add');
                  }}
                  className="bg-gray-300 rounded-full h-[3rem] w-[3rem] font-semibold text-[1.5rem] justify-center items-center flex pb-1 mb-2 mt-3">
                  +
                </button>
                <p>Set Targets</p>
              </div>
            )}
          </div>

          {/* Right Fixed Panel */}
          <div className="w-[300px] space-y-2 fixed right-40 border pl-2 pr-2 pb-2 bg-[#E6E6E6] rounded-lg">
            {/* Header */}
            <div className="bg-[#E6E6E6] rounded-t-lg px-4 py-2 flex justify-center items-center">
              <p className="font-semibold text-black text-sm">Incentive Details</p>
              {incentives && incentives?.data?.length > 0 && (
                <img
                  src={Pencil}
                  alt="edit"
                  className="w-4 h-4 absolute right-3 cursor-pointer "
                  onClick={() => handleEditIncentives(incentives?.data?.[0])}
                />
              )}
            </div>

            {/* New Lead Incentive Card */}
            <div className="bg-white rounded-lg shadow-sm px-4 py-4">
              <p className="text-center font-medium text-sm mb-3">New Booking Incentive Details</p>
              {incentives && incentives?.data?.length > 0 ? (
                <div className="text-sm space-y-3">
                  <div className="flex justify-center gap-2 items-center">
                    <span className="text-black">Weightage</span>
                    <img src={ArrowIcon} alt="arrow" className="w-4 h-4" />
                    <span className="text-black">
                      {incentives?.data?.[0]?.bookingwaightage || 0}%
                    </span>
                  </div>

                  <div className="flex justify-center pt-2">
                    <div className="text-xs text-gray-500 text-right mr-6 space-y-2">
                      {incentives?.data?.[0]?.booking?.map((item, index) => (
                        <div key={index}>{item?.name || '---'}</div>
                      ))}
                    </div>

                    <div className="border-l border-gray-300 mx-4"></div>

                    <div className="text-xs text-gray-500 text-left space-y-2">
                      {incentives?.data?.[0]?.booking?.map((item, index) => (
                        <div key={index}>{item?.percentage || 0}%</div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm space-y-3">
                  <div className="w-full h-[7rem] bg-[#FBFAFB] rounded-lg flex flex-col justify-center items-center">
                    <button
                      onClick={() => {
                        setShowAddIncentivesModal(true);
                        setModalType('Add');
                      }}
                      className="bg-gray-300 rounded-full h-[3rem] w-[3rem] font-semibold text-[1.5rem] justify-center items-center flex pb-1 mb-2 mt-3">
                      +
                    </button>
                    <p>Configure</p>
                  </div>
                </div>
              )}
            </div>

            {/* AR Incentive Card */}
            <div className="bg-white rounded-lg shadow-sm px-4 py-4">
              <p className="text-center font-medium text-sm mb-3">AR Incentive Details</p>
              {incentives && incentives?.data?.length > 0 ? (
                <div className="text-sm space-y-3">
                  <div className="flex justify-center gap-2 items-center">
                    <span className="text-black">Weightage</span>
                    <img src={ArrowIcon} alt="arrow" className="w-4 h-4" />
                    <span className="text-black">{incentives?.data?.[0]?.arwaightage || 0}%</span>
                  </div>

                  <div className="flex justify-center pt-2">
                    <div className="text-xs text-gray-500 text-right mr-6 space-y-2">
                      <div>Type</div>
                      <div>Min Cap</div>
                      <div>Max Cap</div>
                    </div>

                    <div className="border-l border-gray-300 mx-4"></div>

                    <div className="text-xs text-gray-500 text-left space-y-2">
                      <div>{incentives?.data?.[0]?.arMappings?.[0]?.type}</div>
                      <div>{incentives?.data?.[0]?.arMappings?.[0]?.minimum_amount || 0}%</div>
                      <div>{incentives?.data?.[0]?.arMappings?.[0]?.maximum_amount || 0}%</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-sm space-y-3">
                  <div className="w-full h-[7rem] bg-[#FBFAFB] rounded-lg flex flex-col justify-center items-center">
                    <button
                      onClick={() => {
                        setShowAddIncentivesModal(true);
                        setModalType('Add');
                      }}
                      className="bg-gray-300 rounded-full h-[3rem] w-[3rem] font-semibold text-[1.5rem] justify-center items-center flex pb-1 mb-2 mt-3">
                      +
                    </button>
                    <p>Configure</p>
                  </div>
                </div>
              )}
            </div>
          </div>
          </div>
        </div>
      </div>
    </>
  );

  if (embedded) {
    return (
      <>
        {modals}
        {mainBody}
      </>
    );
  }

  return (
    <Layout>
      {modals}
      {mainBody}
    </Layout>
  );
};

export default TargetsAndIncentives;
